import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { UserInfo, LoginRequest, LoginResponse } from '@/types/api'
import { useDashboardStore } from '@/store/dashboardStore'

// ============================================================================
// Auth State Interface
// ============================================================================

export type LogoutReason = 'manual' | 'token_expired' | 'session_401' | null

interface AuthState {
  token: string | null
  user: UserInfo | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  logoutReason: LogoutReason

  // Actions
  login: (credentials: LoginRequest) => Promise<boolean>
  logout: () => void
  logoutWithReason: (reason: LogoutReason) => void
  setToken: (token: string) => void
  setUser: (user: UserInfo) => void
  clearError: () => void
  checkAuth: () => Promise<boolean>
}

// ============================================================================
// API Configuration
// ============================================================================

const API_BASE = '/api'

// ============================================================================
// Auth Store
// ============================================================================

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: true, // Start true — resolved by _resolveAfterHydration below
      error: null,
      logoutReason: null,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null, logoutReason: null })

        try {
          const response = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(credentials),
          })

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            const message = errorData.detail ?? 'Login failed'
            set({ isLoading: false, error: message })
            return false
          }

          const data: LoginResponse = await response.json()
          set({
            token: data.access_token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            logoutReason: null,
          })

          // Fetch user info
          await get().checkAuth()

          return true
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Network error'
          set({ isLoading: false, error: message })
          return false
        }
      },

      logout: () => {
        useDashboardStore.getState().clearConversation()
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          error: null,
          logoutReason: 'manual',
        })
      },

      logoutWithReason: (reason) => {
        useDashboardStore.getState().clearConversation()
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          error: null,
          logoutReason: reason,
        })
      },

      setToken: (token: string) => {
        set({ token, isAuthenticated: true })
      },

      setUser: (user: UserInfo) => {
        set({ user })
      },

      clearError: () => {
        set({ error: null })
      },

      checkAuth: async () => {
        const { token } = get()

        if (!token) {
          set({ isAuthenticated: false, user: null })
          return false
        }

        try {
          const response = await fetch(`${API_BASE}/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })

          if (!response.ok) {
            set({ token: null, user: null, isAuthenticated: false })
            return false
          }

          const user: UserInfo = await response.json()
          set({ user, isAuthenticated: true })
          return true
        } catch {
          set({ token: null, user: null, isAuthenticated: false })
          return false
        }
      },
    }),
    {
      name: 'agentic-auth',
      partialize: (state) => ({
        token: state.token,
      }),
    }
  )
)

// ---------------------------------------------------------------------------
// Post-hydration auth resolution
// Handles all edge cases: normal hydration, missing storage, slow storage,
// and complete hydration failure (timeout safety net).
// ---------------------------------------------------------------------------
function _resolveAfterHydration() {
  const _resolve = () => {
    const { token } = useAuth.getState()
    if (token) {
      useAuth.getState().checkAuth().finally(() => {
        useAuth.setState({ isLoading: false })
      })
    } else {
      useAuth.setState({ isLoading: false, isAuthenticated: false })
    }
  }

  // Path 1: Already hydrated (synchronous localStorage)
  if (useAuth.persist.hasHydrated()) {
    _resolve()
    return
  }

  // Path 2: Wait for hydration to finish
  const unsub = useAuth.persist.onFinishHydration(() => {
    unsub()
    _resolve()
  })

  // Path 3: Safety timeout — if hydration never fires (storage unavailable)
  setTimeout(() => {
    if (useAuth.getState().isLoading) {
      useAuth.setState({ isLoading: false, isAuthenticated: false })
    }
  }, 3000)
}

_resolveAfterHydration()
